import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { CreateFlowField, GetFlowStructure } from 'src/graphql/cashflow'
import { importAlertState } from 'src/redux/actions/alert'
import { importUpdateCashflowNeeded } from 'src/redux/actions/flujocaja'
import { FlowLVL } from 'src/utils/flujoCaja'
import "./cashflowAreaDropdown.scss"


export const CashflowAreaDropdown = ({ editData, onChange, showAddOption = true, multiple = false, limit = [], onlyCategories = false, onOpenChange }) => {
  const { t } = useTranslation('finance')
  const dispatch = useDispatch()
  const { selectedCompany } = useSelector((state) => state.companyList)
  const companyId = selectedCompany?.id
  const { updateCashflowNeeded } = useSelector((state) => state.flujocaja)

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedField, setSelectedField] = useState()
  const [selectedCategory, setSelectedCategory] = useState()
  const [isOpen, setIsOpen] = useState(false)
  const [newArea, setNewArea] = useState()
  const newFieldName = useRef()
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  const categoryDropdownRef = useRef(null)

  const { data, fetching, reexecuteFlow } = GetFlowStructure({ companyId })
  const { executeMutation: createFlowFieldMutation } = CreateFlowField()

  const categories = useMemo(() => {
    if (!companyId) return []
    if (fetching || !data) return []
    if (!Array.isArray(data?.getFlowStructure) || data.getFlowStructure.length === 0) return []

    const [flow] = data.getFlowStructure
    if (!flow?.report) return []

    const IncomeCategories = flow.report?.[FlowLVL.INCOME] || []
    const ExpenseCategories = flow.report?.[FlowLVL.EXPENSE] || []
    let result = []

    if (limit.length == 0) result = [...IncomeCategories, ...ExpenseCategories]
    if (limit.includes(FlowLVL.INCOME)) result = [...IncomeCategories]
    if (limit.includes(FlowLVL.EXPENSE)) result = [...ExpenseCategories]

    return result
  }, [companyId, fetching, data?.getFlowStructure, limit])

  // Transform to dropdown options
  const options = useMemo(() => {
    if (onlyCategories) {
      return categories.reduce((acc, categoryRow, index) => {
        acc.push({
          key: `${categoryRow.cashflowCategoryId}-${index}`,
          value: categoryRow.cashflowCategoryId,
          text: categoryRow.label,
          isSection: false,
        })
        return acc
      }, [])
    }

    if (multiple) {
      return categories.reduce((acc, categoryRow) => {
        // Add section header
        acc.push({
          key: `section-${categoryRow.cashflowCategoryId}`,
          value: null,
          text: categoryRow.label,
          isSection: true,
          categoryId: categoryRow.cashflowCategoryId,
        })

        // Add fields
        const fields = categoryRow.fields.map((field, index) => ({
          key: `${field.cashflowAreaId}-${index}`,
          value: field.cashflowAreaId,
          text: field.label,
          isSection: false,
          field: field,
        }))

        acc = [...acc, ...fields]
        return acc
      }, [])
    }

    // For single select, return structured options with categories and fields
    return categories.reduce((acc, categoryRow) => {
      acc.push({
        key: `category-${categoryRow.cashflowCategoryId}`,
        value: null,
        text: categoryRow.label,
        isSection: true,
        category: categoryRow,
      })

      categoryRow.fields.forEach((field, index) => {
        acc.push({
          key: `${field.cashflowAreaId}-${index}`,
          value: field.cashflowAreaId,
          text: field.label,
          isSection: false,
          field: field,
        })
      })

      return acc
    }, [])
  }, [categories, onlyCategories, multiple])

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options

    const query = searchQuery.toLowerCase()

    if (onlyCategories) {
      return options.filter(opt => 
        opt.text.toLowerCase().includes(query)
      )
    }

    if (multiple) {
      return options.filter(opt => {
        if (opt.isSection) {
          // Show section if any field matches
          const category = categories.find(cat => cat.cashflowCategoryId === opt.categoryId)
          return category?.fields.some(field => 
            field.label.toLowerCase().includes(query)
          )
        }
        return opt.text.toLowerCase().includes(query)
      })
    }

    // For single select, filter categories and their fields
    const filtered = []
    categories.forEach(categoryRow => {
      const showHeader = categoryRow.fields.some(field => 
        field.label.toLowerCase().includes(query)
      ) || categoryRow.label.toLowerCase().includes(query)

      if (showHeader) {
        filtered.push({
          key: `category-${categoryRow.cashflowCategoryId}`,
          value: null,
          text: categoryRow.label,
          isSection: true,
          category: categoryRow,
        })

        categoryRow.fields.forEach((field, index) => {
          if (field.label.toLowerCase().includes(query)) {
            filtered.push({
              key: `${field.cashflowAreaId}-${index}`,
              value: field.cashflowAreaId,
              text: field.label,
              isSection: false,
              field: field,
            })
          }
        })
      }
    })

    return filtered
  }, [options, searchQuery, categories, onlyCategories, multiple])

  useEffect(() => {
    if (updateCashflowNeeded?.value) {
      reexecuteFlow({ requestPolicy: 'network-only' })
      dispatch(importUpdateCashflowNeeded({ value: false, reload: null }))
    }
  }, [updateCashflowNeeded?.value, reexecuteFlow, dispatch])

  useEffect(() => {
    if (editData?.cashflowAreaId) {
      // Preserve text when we already have the same selection (e.g. after creating new area)
      const preserveText = selectedField?.cashflowAreaId == editData.cashflowAreaId && selectedField?.text
      const structuredOption = {
        key: editData.cashflowAreaId,
        value: editData.cashflowAreaId,
        text: editData.label || preserveText || '',
        isSection: false,
        field: editData,
        cashflowAreaId: editData.cashflowAreaId,
      }
      setSelectedField(structuredOption)
    }
  }, [editData?.cashflowAreaId, editData?.label])

  // Update selectedField text when data becomes available
  useEffect(() => {
    if (selectedField?.cashflowAreaId && (!selectedField?.text || selectedField.text === '') && data?.getFlowStructure) {
      // Inline label lookup to avoid dependency on getLabel function
      const [flow] = data.getFlowStructure
      const report = flow.report
      const cashflowAreaId = selectedField.cashflowAreaId

      const label = [FlowLVL.INCOME, FlowLVL.EXPENSE].reduce((acc, el) => {
        const LVL = report[el]
        const result = LVL.reduce((acc1, categoryRow) => {
          const selectedFieldLabel = categoryRow.fields.filter((field) => field.cashflowAreaId == cashflowAreaId)
          if (selectedFieldLabel[0]) acc1 = selectedFieldLabel[0].label
          return acc1
        }, '')
        if (result) acc = result
        return acc
      }, '')

      if (label) {
        setSelectedField(prev => ({
          ...prev,
          text: label
        }))
      }
    }
  }, [data?.getFlowStructure, selectedField?.cashflowAreaId])

  // Handle new area selection after creation
  useEffect(() => {
      if (newArea && categories.length > 0) {
      let newAreaField
      const newAreaTrimmed = newArea?.trim()
      for (let cat of categories) {
        for (let area of cat.fields) {
          if (area.label?.trim() === newAreaTrimmed) {
            newAreaField = {
              key: area.cashflowAreaId,
              value: area.cashflowAreaId,
              text: area.label,
              isSection: false,
              field: area,
              cashflowAreaId: area.cashflowAreaId,
            }
            break
          }
        }
        if (newAreaField) break
      }

      if (newAreaField?.cashflowAreaId) {
        setSelectedField(newAreaField)
        onChange(String(newAreaField.cashflowAreaId))
        setIsOpen(false)
        setNewArea(null)
      }
    }
  }, [newArea, categories, onChange])

  // Notify parent when dropdown opens/closes
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen)
    }
  }, [isOpen, onOpenChange])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const getLabel = (cashflowAreaId) => {
    if (!data?.getFlowStructure || !Array.isArray(data.getFlowStructure) || data.getFlowStructure.length === 0) return ''
    const [flow] = data.getFlowStructure
    const report = flow?.report
    if (!report) return ''

    const label = [FlowLVL.INCOME, FlowLVL.EXPENSE].reduce((acc, el) => {
      const LVL = report[el]
      const result = LVL.reduce((acc1, categoryRow) => {
        const selectedFieldLabel = categoryRow.fields.filter((field) => field.cashflowAreaId == cashflowAreaId)
        if (selectedFieldLabel[0]) acc1 = selectedFieldLabel[0].label
        return acc1
      }, '')
      if (result) acc = result
      return acc
    }, '')
    return label
  }

  const addNewField = async () => {
    try {
      const fieldLabel = newFieldName.current?.value?.trim()
      if (!fieldLabel) {
        return dispatch(
          importAlertState({
            content: `Por favor ingrese el nombre del área.`,
            state: "error",
            duration: 4,
          })
        )
      }

      if (!data?.getFlowStructure) return false
      const [flow] = data.getFlowStructure

      const variables = { 
        companyId, 
        id: flow.id, 
        fieldLabel, 
        cashflowCategoryId: selectedCategory 
      }
      const response = await createFlowFieldMutation(variables)

      if (response) {
        const status = response.data.createFlowField.status
        const message = response.data.createFlowField.message

        if (status) {
          await reexecuteFlow({ requestPolicy: "network-only" })
          setNewArea(fieldLabel)
          newFieldName.current.value = ''
          dispatch(importUpdateCashflowNeeded({ value: false, reload: null }))
        } else {
          dispatch(
            importAlertState({
              content: message,
              state: 'error',
              duration: 4,
            })
          )
        }

        return true
      }
    } catch (e) {
      console.error(e.message)
      dispatch(
        importAlertState({
          content: 'Error al crear el área',
          state: 'error',
          duration: 4,
        })
      )

      return false
    }
  }

  // Handle option selection
  const handleSelect = (option) => {
    if (option.isSection) return

    setSelectedField(option)
    setIsOpen(false)
    setSearchQuery('')
    
    if (onChange) {
      onChange(option.value)
    }
  }

  // Handle input change for search
  const handleInputChange = (e) => {
    const query = e.target.value
    setSearchQuery(query)
    if (!isOpen) {
      setIsOpen(true)
    }
  }

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true)
  }

  // Handle key navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchQuery('')
      inputRef.current?.blur()
    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault()
      const firstSelectableOption = filteredOptions.find(opt => !opt.isSection)
      if (firstSelectableOption) {
        handleSelect(firstSelectableOption)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!isOpen) {
        setIsOpen(true)
      }
    }
  }

  // Handle clearing selection
  const handleClear = () => {
    setSelectedField(null)
    setIsOpen(false)
    setSearchQuery('')
    
    if (onChange) {
      onChange(null)
    }
  }

  // Category options for add new area dropdown
  const categoryOptions = useMemo(() => {
    return categories.map((categoryRow) => ({
      key: categoryRow.cashflowCategoryId,
      value: categoryRow.cashflowCategoryId,
      text: categoryRow.label,
      isSection: false,
    }))
  }, [categories])

  const selectedText = selectedField?.text || (selectedField?.cashflowAreaId ? getLabel(selectedField.cashflowAreaId) : '')
  const displayValue = isOpen ? searchQuery : selectedText
  const showPlaceholder = !selectedText && !isOpen

  if (fetching || !data) {
    return (
      <div className="cashflow-area-dropdown">
        <div className="dropdown-wrapper">
          <input
            type="text"
            className="dropdown-input"
            placeholder={isOpen ? 'No hay áreas para mostrar' : 'Cargando...'}
            disabled
          />
        </div>
      </div>
    )
  }

  // Multiple selection mode - simplified for now (can be enhanced later)
  if (multiple) {
    return (
      <div className="cashflow-area-dropdown">
        <div className="dropdown-wrapper">
          <input
            type="text"
            className="dropdown-input"
            placeholder="Seleccione Área..."
            disabled
            readOnly
          />
        </div>
        <div className="dropdown-menu">
          <div className="dropdown-no-results">
            Modo múltiple - funcionalidad en desarrollo
          </div>
        </div>
      </div>
    )
  }

  return (
    <div 
      ref={dropdownRef}
      className={`cashflow-area-dropdown ${isOpen ? 'open' : ''}`}
    >
      <div className="dropdown-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="dropdown-input"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={showPlaceholder ? (onlyCategories ? t('cashflow.selectCategory') : t('cashflow.selectArea')) : ''}
          disabled={fetching}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />
        {selectedField && (
          <span 
            className="dropdown-clear"
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
            title={t('cashflow.clearSelection')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" width="16" height="16">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 6l8 8M14 6l-8 8"/>
            </svg>
          </span>
        )}
        <span className="dropdown-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4"/>
          </svg>
        </span>
        {fetching && (
          <span className="dropdown-loading">Loading...</span>
        )}
      </div>
      {isOpen && (
        <div className="dropdown-menu">
          {showAddOption && (
            <div className="add-area-section">
              <div className="add-area-header">{t('cashflow.addNewArea')}</div>
              <div className="add-area-items">
                <div 
                  ref={categoryDropdownRef}
                  className="category-dropdown-wrapper"
                  onClick={(e) => e.stopPropagation()}
                >
                  <select
                    className="category-select"
                    value={selectedCategory || ''}
                    onChange={(e) => {
                      e.stopPropagation()
                      setSelectedCategory(e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">{t('cashflow.categoryPlaceholder')}</option>
                    {categoryOptions.map(opt => (
                      <option key={opt.key} value={opt.value}>
                        {opt.text}
                      </option>
                    ))}
                  </select>
                </div>
                <input
                  ref={newFieldName}
                  className="add-area-input"
                  id="inputArea"
                  placeholder={t('cashflow.newArea')}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => e.stopPropagation()}
                  type="text"
                />
                <button
                  className="add-area-button"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (selectedCategory && newFieldName.current?.value) {
                      addNewField()
                    }
                  }}
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="18" height="18">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v8M8 12h8"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
          {filteredOptions.length > 0 ? (
            <ul className="dropdown-options" role="listbox">
              {filteredOptions.map((option) => (
                <li
                  key={option.key}
                  className={`dropdown-option ${option.isSection ? 'section-header' : ''} ${selectedField?.value === option.value ? 'selected' : ''}`}
                  onClick={() => !option.isSection && handleSelect(option)}
                  role={option.isSection ? 'presentation' : 'option'}
                  aria-selected={option.isSection ? undefined : selectedField?.value === option.value}
                >
                  <span className="option-text">{option.text}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="dropdown-no-results">
              {searchQuery ? "No se encontraron resultados." : (onlyCategories ? "No hay categorías disponibles." : "No hay áreas disponibles.")}
            </div>
          )}
        </div>
      )}
    </div>
  )
}